import React, { useEffect, useMemo, useState, useCallback } from "react";
import "./Workspace.css";
import { useChartsStore } from "~/store/chartStore";
import { useSidebarFavoritesStore } from "~/store/sidebarFavoritesStore";
import ObjectsSidebar from "./ObjectsSidebar";
import useWorkspaceStore from "~/store/workspaceStore";
import RouterOperationsSidebar from "./RouterOperationsSidebar";
import useKeyboardShortcuts from "~/hooks/useKeyboardShortcuts";
import KeyboardShortcutsModal from "../Modals/KeyboardShortcutsModal";
import { useOrderDefaults } from "~/hooks/useOrderDefaults";
import { isMobilePhone } from "~/utils/mobileDetection";
import DockModeLayout from "./DockModeLayout";
import WorkspaceLoadingOverlay from "./WorkspaceLoadingOverlay";
import { dispatchChartSizingResize } from "../ChartWrapper/utils/chartSizingDebug";

const Workspace = () => {
  const charts = useChartsStore((state) => state.charts);
  const isRouterOperationsSidebarOpen = useWorkspaceStore(
    (state) => state.isRouterOperationsSidebarOpen,
  );
  const dockLayoutKey = useWorkspaceStore((state) => state.dockLayoutKey);

  // Keyboard shortcuts help modal state
  const [shortcutsModalVisible, setShortcutsModalVisible] = useState(false);

  const setFocusedChart = useChartsStore((state) => state.setFocusedChart);
  const selectedChartId = useChartsStore(
    (state) => state.getSelectedChart()?.id || null,
  );
  const selectedChartSymbol = useChartsStore(
    (state) => state.getSelectedChart()?.symbol || null,
  );

  // Issue #100: Set active chart for per-tab quantity tracking
  const { setActiveChart } = useOrderDefaults();
  useEffect(() => {
    if (selectedChartId && selectedChartSymbol) {
      setActiveChart(selectedChartId, selectedChartSymbol);
    }
  }, [selectedChartId, selectedChartSymbol, setActiveChart]);

  const favorites = useSidebarFavoritesStore((state) => state.favorites);
  const toggleFavorite = useSidebarFavoritesStore(
    (state) => state.toggleFavorite,
  );
  const toggleSidebar = useSidebarFavoritesStore(
    (state) => state.toggleSidebar,
  );
  const toggleRouterOperationsSidebar = useWorkspaceStore(
    (state) => state.toggleRouterOperationsSidebar,
  );
  const toggleIndicatorsDropdown = useWorkspaceStore(
    (state) => state.toggleIndicatorsDropdown,
  );

  // Toggle browser fullscreen mode (hides address bar, bookmarks, taskbar)
  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen();
    }
  }, []);

  // Focus Nth chart in dock mode by setting focusedChartId
  const focusDockPanel = useCallback(
    (index) => {
      if (index < charts.length) {
        setFocusedChart(charts[index].id);
      }
    },
    [charts, setFocusedChart],
  );

  // Keyboard shortcuts configuration
  const shortcuts = useMemo(
    () => ({
      // Indicators dropdown
      "Ctrl+I": toggleIndicatorsDropdown,
      // Sidebar toggles
      "Ctrl+D": toggleSidebar,
      "Ctrl+R": toggleRouterOperationsSidebar,
      // Fullscreen
      F11: toggleFullscreen,
      // Help
      "Ctrl+Shift+/": () => setShortcutsModalVisible(true),
      // Focus dock panels
      "Ctrl+1": () => focusDockPanel(0),
      "Ctrl+2": () => focusDockPanel(1),
      "Ctrl+3": () => focusDockPanel(2),
      "Ctrl+4": () => focusDockPanel(3),
      "Ctrl+5": () => focusDockPanel(4),
      "Ctrl+6": () => focusDockPanel(5),
      "Ctrl+7": () => focusDockPanel(6),
      "Ctrl+8": () => focusDockPanel(7),
      "Ctrl+9": () => focusDockPanel(8),
      "Ctrl+0": () => focusDockPanel(9),
    }),
    [
      charts.length,
      focusDockPanel,
      toggleIndicatorsDropdown,
      toggleSidebar,
      toggleRouterOperationsSidebar,
      toggleFullscreen,
    ],
  );

  // Initialize keyboard shortcuts
  useKeyboardShortcuts(shortcuts);

  // Dispatch resize after fullscreen toggle (Issue #243)
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      dispatchChartSizingResize("workspace.fullscreen-change.immediate");
      setTimeout(
        () =>
          dispatchChartSizingResize(
            "workspace.fullscreen-change.delayed-150ms",
          ),
        150,
      );
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const handleAddFavorite = (object) => {
    toggleFavorite(object);
  };

  const [interactiveApi, setInteractiveApi] = React.useState(null);
  const handleInteractiveApiReady = React.useCallback((api) => {
    setInteractiveApi(api);
  }, []);

  const routerOperationsPanelStyle = useMemo(
    () => ({
      width: 82,
      minWidth: 82,
      maxWidth: 82,
      display: "flex",
      flexDirection: "column",
      height: "100%",
      flexShrink: 0,
      background: "#f4f6fa",
    }),
    [],
  );

  const workspaceContainerStyle = useMemo(
    () => ({
      display: "flex",
      height: "100%",
      width: "100%",
      overflow: "hidden",
    }),
    [],
  );

  const contentChartStyle = useMemo(
    () => ({
      display: "flex",
      height: "100%",
      flex: 1,
      minWidth: 0,
    }),
    [],
  );

  const chartContainerStyle = useMemo(
    () => ({
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      position: "relative",
    }),
    [],
  );

  return (
    <div style={workspaceContainerStyle}>
      <div style={contentChartStyle}>
        <ObjectsSidebar
          handleAddFavorite={handleAddFavorite}
          favorites={favorites}
          interactiveApi={interactiveApi}
        />
        <div style={chartContainerStyle}>
          <DockModeLayout
            key={dockLayoutKey}
            charts={charts}
            onInteractiveApiReady={handleInteractiveApiReady}
          />
          <WorkspaceLoadingOverlay />
        </div>
      </div>

      {/* Right: Router Operations Sidebar - Fixed width, no splitter */}
      {isRouterOperationsSidebarOpen && !isMobilePhone() && (
        <div
          className="router-operations-panel-fixed"
          style={routerOperationsPanelStyle}
        >
          <RouterOperationsSidebar />
        </div>
      )}

      {/* Keyboard Shortcuts Help Modal */}
      <KeyboardShortcutsModal
        visible={shortcutsModalVisible}
        onHide={() => setShortcutsModalVisible(false)}
      />
    </div>
  );
};

export default Workspace;
